import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { updateInfluencer } from 'services/api/influencer'
import { Influencer } from 'types/influencer'
import { getErrorMessage } from 'utils/helpers'
import CitySelection from './CitySelection'
import './InfluencerProfileCompleteForm.scss'

export type ProfileFormValues = {
    name: string
    age: string
    gender: 'M' | 'F'
    location: string
}

const toFormValues = (influencer: Influencer): ProfileFormValues => ({
    name: influencer.name ?? '',
    age: influencer.age ? String(influencer.age) : '',
    gender: influencer.gender === 'F' ? 'F' : 'M',
    location: influencer.location ?? '',
})

const isValidAge = (age: string) => {
    const value = Number(age)
    return Number.isInteger(value) && value > 0 && value <= 120
}

const isProfileFormComplete = (form: ProfileFormValues, hasExistingDob: boolean) =>
    !!form.name.trim() &&
    !!form.location.trim() &&
    (hasExistingDob || isValidAge(form.age))

interface InfluencerProfileCompleteFormProps {
    hubId: string
    influencer: Influencer
    onUpdated: (influencer: Influencer) => void
}

const InfluencerProfileCompleteForm: React.FC<InfluencerProfileCompleteFormProps> = ({
    hubId,
    influencer,
    onUpdated,
}) => {
    const hasExistingDob = !!influencer.dob?.trim()
    const [form, setForm] = useState<ProfileFormValues>(() => toFormValues(influencer))
    const [saving, setSaving] = useState(false)
    const [errors, setErrors] = useState<Partial<Record<keyof ProfileFormValues, string>>>({})

    useEffect(() => {
        setForm(toFormValues(influencer))
        setErrors({})
    }, [influencer])

    const isFormComplete = useMemo(
        () => isProfileFormComplete(form, hasExistingDob),
        [form, hasExistingDob]
    )

    const validate = () => {
        const next: Partial<Record<keyof ProfileFormValues, string>> = {}
        if (!form.name.trim()) next.name = 'Name is required'
        if (!hasExistingDob && !isValidAge(form.age)) next.age = 'Age is required'
        if (!form.location.trim()) next.location = 'City is required'
        setErrors(next)
        return Object.keys(next).length === 0
    }

    const handleSave = async () => {
        if (!validate()) return
        try {
            setSaving(true)
            const updated = await updateInfluencer(hubId, influencer.uuid, {
                name: form.name.trim(),
                ...(hasExistingDob ? {} : { age: Number(form.age) }),
                gender: form.gender,
                location: form.location.trim(),
            })
            toast.success('Profile updated successfully.')
            onUpdated(updated)
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to update profile. Please try again.'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="influencer-profile-complete">
            <div className="influencer-profile-complete__body">
                <div className="influencer-profile-complete__field">
                    <label className="influencer-profile-complete__label">
                        Name<span className="influencer-profile-complete__required">*</span>
                    </label>
                    <div className="influencer-profile-complete__input-wrap">
                        <i className="ri-user-line influencer-profile-complete__input-icon" aria-hidden />
                        <input
                            type="text"
                            className={`form-control${errors.name ? ' is-invalid' : ''}`}
                            value={form.name}
                            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter name"
                        />
                    </div>
                    {errors.name && <div className="influencer-profile-complete__error">{errors.name}</div>}
                </div>

                {!hasExistingDob && (
                    <div className="influencer-profile-complete__field">
                        <label className="influencer-profile-complete__label">
                            Age<span className="influencer-profile-complete__required">*</span>
                        </label>
                        <div className="influencer-profile-complete__input-wrap">
                            <i className="ri-calendar-line influencer-profile-complete__input-icon" aria-hidden />
                            <input
                                type="number"
                                min={1}
                                max={120}
                                className={`form-control${errors.age ? ' is-invalid' : ''}`}
                                value={form.age}
                                onChange={(e) => setForm((prev) => ({ ...prev, age: e.target.value }))}
                                placeholder="Enter age"
                            />
                        </div>
                        {errors.age && <div className="influencer-profile-complete__error">{errors.age}</div>}
                    </div>
                )}

                <div className="influencer-profile-complete__field">
                    <label className="influencer-profile-complete__label">
                        Gender<span className="influencer-profile-complete__required">*</span>
                    </label>
                    <div className="influencer-profile-complete__gender-group">
                        {([{label: 'Male', value: 'M'}, {label: 'Female', value: 'F'}] as const).map((gender) => (
                            <button
                                key={gender.value}
                                type="button"
                                className={`influencer-profile-complete__gender-btn${
                                    form.gender === gender.value
                                        ? ' influencer-profile-complete__gender-btn--active'
                                        : ''
                                }`}
                                onClick={() => setForm((prev) => ({ ...prev, gender: gender.value }))}
                            >
                                {gender.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="influencer-profile-complete__field">
                    <label className="influencer-profile-complete__label">
                        City<span className="influencer-profile-complete__required">*</span>
                    </label>
                    <CitySelection
                        value={form.location}
                        invalid={!!errors.location}
                        onChange={(location) => {
                            setForm((prev) => ({ ...prev, location }))
                            if (errors.location) {
                                setErrors((prev) => ({ ...prev, location: undefined }))
                            }
                        }}
                    />
                    {errors.location && (
                        <div className="influencer-profile-complete__error">{errors.location}</div>
                    )}
                </div>
            </div>
            <div className="influencer-profile-complete__footer">
                <button
                    type="button"
                    className="theme-btn btn-primary influencer-profile-complete__save w-100"
                    disabled={saving || !isFormComplete}
                    onClick={handleSave}
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div>
    )
}

export default InfluencerProfileCompleteForm
